import { EVENT_PARAM_KEY as EPK } from '../constants/EventParamKey'
import {
  EventScreenType,
  safeMatch,
  safeDecodeURIComponent,
} from '../core/EventScreenManager'
import extractUrl from '../../../lib/extractUrl'
import routeController from '../../../routes'

export type ProductObject = {
  details?: ProductDetails | null
  checkoutAddress?: CheckoutAddress | null
  productPosition?: number | null
  ctaSublocation?: string | null
  title?: string | null
}

export type ProductBcrsFee = {
  text: string
}

export type ProductDetails = {
  id?: number | null
  clientItemId?: string | null
  name?: string | null
  brand?: Brand | null
  primaryCategory?: Category | null
  offers?: Offer[] | null
  reviews?: Review | null
  seller?: Seller | null
  sellerInfo?: SellerInfo | null
  storeSpecificData?: StoreSpecific[] | null
  bcrsFee?: ProductBcrsFee | null
  depositSKU?: ProductDepositSKU | null
  metaData?: MetaData | null
  beverageContainerDeposit?: boolean | null
}

export type Brand = {
  name?: string | null
}

export type CheckoutAddress = {
  storeId?: string | null
  storeType?: string | null
  storeName?: string | null
  clientId?: string | null
}

export type Category = {
  id?: string | null
  name?: string | null
  parentCategory?: Category | null
}

export type Offer = {
  id?: string | null
  type?: string | null
  discount?: number | null
  description?: string | null
  appliedCount?: number | null
  details?: OfferDetails | null
}

export type OfferDetails = {
  offerType?: string | null
  description?: string | null
}

export type Review = {
  statistics?: Statistics | null
}

export type Statistics = {
  averageDisplay?: string | null
}

export type Seller = {
  name?: string | null
}

export type SellerInfo = {
  id?: string | null
}

export type StoreSpecific = {
  stock?: number | null
  unlimitedStock?: boolean | null
  mrp?: string | null
  discount?: string | null
}

export type MetaData = {
  DisplayUnit?: string | null
  'Dietary Attributes'?: string[] | null
  isBCRS?: boolean
}

export type ProductDepositSKU = {
  price?: number | null
}

const getStoreSpecific = (
  details: ProductDetails | null | undefined
): StoreSpecific | null => {
  const { storeSpecificData } = details ?? {}
  return storeSpecificData?.length ? storeSpecificData[0] : null
}

const discountedPrice = (
  storeSpecific: StoreSpecific | undefined | null
): number => {
  if (storeSpecific?.mrp && storeSpecific.discount) {
    const discount = Number(storeSpecific.mrp) - Number(storeSpecific.discount)
    return Number(discount.toFixed(2))
  }
  return 0
}

const priceRetail = (
  storeSpecific: StoreSpecific | undefined | null
): number => {
  if (storeSpecific?.mrp) {
    return Number(storeSpecific.mrp)
  }
  return 0
}

const isMarketplaceItem = (
  details: ProductDetails | undefined | null
): boolean => {
  if (details?.seller) {
    return Object.keys(details.seller).length > 0
  }
  return false
}

const getRating = (reviews: Review | null | undefined): number | null => {
  const average = reviews?.statistics?.averageDisplay
  return average ? Number(average) : null
}

const getDietaryType = (
  metaData: MetaData | null | undefined
): string | null => {
  if (metaData) {
    const attrs = metaData['Dietary Attributes']
    if (attrs && attrs.length > 0) {
      return attrs.join()
    }
  }
  return null
}

const getProductOfferApplied = (
  offer: Offer[] | null | undefined
): (boolean | null)[] => {
  return (
    offer?.map((offer: Offer) => {
      return offer.appliedCount ? true : null
    }) ?? []
  )
}

const getProductOfferIds = (
  offers: Offer[] | null | undefined
): (string | null)[] => {
  return (
    offers?.map((offer: Offer) => {
      return offer.id ? `${offer.id}` : null
    }) ?? []
  )
}

const getProductOfferTypes = (
  offers: Offer[] | null | undefined
): (string | null)[] => {
  return (
    offers?.map((offer: Offer) => {
      if (offer.details) {
        return offer.details.offerType ?? null
      } else {
        return offer.type ?? null
      }
    }) ?? []
  )
}

const getProductOfferDescriptions = (
  offers: Offer[] | null | undefined
): (string | null)[] => {
  return (
    offers?.map((offer: Offer) => {
      if (offer.details) {
        return offer.details?.description ?? null
      } else {
        return offer.description ?? null
      }
    }) ?? []
  )
}

const getProductOfferAmounts = (
  offers: Offer[] | null | undefined
): (number | null)[] => {
  return (
    offers?.map((offer: Offer) => {
      if (offer.appliedCount && offer.discount) {
        return Number((offer.discount * offer.appliedCount).toFixed(2))
      } else {
        return 0
      }
    }) ?? []
  )
}

const getScreenSectionFromRoute = (
  routeName: string | undefined,
  tag: string | undefined,
  title: string | null | undefined
): string | null => {
  if (!title) {
    return null
  }

  switch (routeName) {
    case 'promotions':
      return `promotion-collection | ${tag ? safeDecodeURIComponent(tag) : EventScreenType.All
        }`
    case 'category':
      return `category-collection | ${title}`
    default:
      return `product-collection | ${title}`
  }
}

export const mapProductTrackingData = (
  data: ProductObject,
  inventoryStatus?: string | null,
  quantity?: number | null,
  includeCta?: boolean | null
) => {
  try {
    const { productPosition, details, checkoutAddress, title = '' } = data
    const { primaryCategory, offers, reviews, metaData } = details ?? {}
    const storeSpecific = getStoreSpecific(data.details)
    const product = {
      [EPK.PRODUCT_ID_DBP]: details?.id?.toString() ?? null,
      [EPK.PRODUCT_ID_SAP]: details?.clientItemId?.toString() ?? null,
      [EPK.PRODUCT_NAME]: details?.name ?? null,
      [EPK.BRAND]: details?.brand?.name ?? null,
      [EPK.FULFILMENT_TYPE]: checkoutAddress?.storeType?.toLowerCase() ?? null,
      [EPK.IS_MARKETPLACE_ITEM]: isMarketplaceItem(details),
      [EPK.PRICE_RETAIL]: priceRetail(storeSpecific),
      [EPK.PRICE_DISCOUNTED]: discountedPrice(storeSpecific),
      [EPK.NUMBER_OF_PROMOTIONS]: offers?.length ?? 0,
      [EPK.INVENTORY_STATUS]: inventoryStatus?.toLowerCase(),
      [EPK.DISPLAY_UNIT]: metaData?.DisplayUnit ?? null,
      [EPK.DIETARY_TYPE]: getDietaryType(metaData),
      [EPK.RANK]: productPosition ? Number(productPosition) : 1,
      [EPK.RATING]: getRating(reviews),
      [EPK.CATEGORY_L1_ID]: primaryCategory?.id?.toString() ?? null,
      [EPK.CATEGORY_L1]: primaryCategory?.name ?? null,
      [EPK.CATEGORY_L2_ID]:
        primaryCategory?.parentCategory?.id?.toString() ?? null,
      [EPK.CATEGORY_L2]: primaryCategory?.parentCategory?.name ?? null,
      [EPK.CATEGORY_L3_ID]:
        primaryCategory?.parentCategory?.parentCategory?.id?.toString() ?? null,
      [EPK.CATEGORY_L3]:
        primaryCategory?.parentCategory?.parentCategory?.name ?? null,
      [EPK.SELLER_ID]: details?.sellerInfo?.id?.toString() ?? null,
      [EPK.SELLER]: details?.seller?.name ?? null,
      [EPK.STORE_ID_DBP]: checkoutAddress?.storeId?.toString() ?? null,
      [EPK.STORE_ID_SAP]: checkoutAddress?.clientId?.toString() ?? null,
      [EPK.STORE]: checkoutAddress?.storeName ?? null,
      [EPK.PRODUCT_OFFER_ID]: getProductOfferIds(offers),
      [EPK.PRODUCT_OFFER_TYPE]: getProductOfferTypes(offers),
      [EPK.PRODUCT_OFFER_DESCRIPTION]: getProductOfferDescriptions(offers),
      [EPK.PRODUCT_OFFER_AMOUNT]: getProductOfferAmounts(offers),
      [EPK.PRODUCT_OFFER_APPLIED]: getProductOfferApplied(offers),
      [EPK.CAMPAIGN_PREORDER_ID]: null,
      [EPK.CAMPAIGN_PREORDER]: null,
      [EPK.DELIVERY_AVAILABILITY]: [], // This is a new variable, does not exist yet.
    }
    if (quantity) {
      product[EPK.QUANTITY] = quantity
    }
    if (includeCta === true) {
      product[EPK.SCREEN_SECTION] = data.ctaSublocation
        ? data.ctaSublocation
        : null
    }

    // const match = routeController.match(extractUrl())
    const match = safeMatch(routeController, extractUrl())
    if (!match) {
      return null
    }
    const routeBasedScreenSection = getScreenSectionFromRoute(
      match.route?.name,
      match.query?.tag,
      title
    )
    if (routeBasedScreenSection) {
      product[EPK.SCREEN_SECTION] = routeBasedScreenSection
    }

    if (!product[EPK.SCREEN_SECTION]) {
      product[EPK.SCREEN_SECTION] = 'product-collection'
    }

    const hasValidBcrsFlag = !!details?.metaData?.isBCRS
    const hasValidBcrsFeeText = !!details?.bcrsFee?.text
    const hasValidDepositSKU = Number(details?.depositSKU?.price) > 0
    // for product view impressions, the payload already been processed so we only need to check the beverageContainerDeposit
    const hasValidBcrsBeverageContainerDeposit = !!details?.beverageContainerDeposit
    product[EPK.BCRS] = (hasValidBcrsFlag && (hasValidBcrsFeeText || hasValidDepositSKU)) || hasValidBcrsBeverageContainerDeposit


    return product
  } catch (e) {
    console.log(e)
    return null
  }
}
